import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component, ElementRef, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
    ApiRequestService,
    DialogComponent,
    DialogConfiguration,
    DialogService,
    FileexplorerComponent,
    generateOneSegmentSlug,
    MAT_ICON,
    MetaComponent,
    MetaService,
    TinyMCEHtmlEditorComponent,
    TOAST_STATE,
    ToastmessageService,
    TranslationPipe,
    TranslationService
} from '@prekog/common-ui';
import { Observable, switchMap, throwError } from 'rxjs';
import { GetSpecialityResponse } from '../..//pricelist/getspecialityresponse';
import { UploadFileResponse } from '../../doctors/updatedoctor/uploadfileresponse';
import { Examination } from '../../pricelist/examination';
import { GetSpecialityRequest } from '../../pricelist/getspecialityrequest';
import { DeleteExaminationByIdRequest } from '../../pricelist/models/deleteexaminationbyidrequest';
import { SpecialityInformation } from '../../smarty/specialityinformation';
import { Speciality } from '../../specialitypricelist/speciality';
import { AddexaminationDialogComponent } from '../addspeciality/addexaminationdialog/addexaminationdialog.component';
import { AddExaminationDialogResult } from '../addspeciality/addexaminationdialog/addexaminationdialogresult';
import { DoctorWithChecked } from '../addspeciality/doctorwithchecked';
import { GetSpecialityTypesResponse } from '../addspeciality/getspecialitytypesresponse';
import { SpecialityType } from '../addspeciality/specialitytype';
import { AvailableDoctor } from './availabledoctor';
import { DoctorSpeciality } from './doctorspeciality';
import { GetAvailableDoctorsRequest as GetgetAvailableDoctorsUrlRequest } from './getavailabledoctorsrequest';
import { GetAvailableDoctorsResponse as GetgetAvailableDoctorsUrlResponse } from './getavailabledoctorsresponse';
import { DeleteDoctorSpecialityRequest } from './models/deletedoctorspecialityrequest';
import { UpdateSpecialityRequest } from './updatespecialityrequest';

@Component({
    selector: 'app-updatespeciality',
    templateUrl: './updatespeciality.component.html',
    styleUrls: ['./updatespeciality.component.scss'],
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatIconModule,
        CommonModule,
        RouterModule,
        MatCheckboxModule,
        MatRadioModule,
        FileexplorerComponent,
        TinyMCEHtmlEditorComponent,
        FormsModule,
        MetaComponent,
        TranslationPipe
    ]
})
export class UpdatespecialityComponent implements OnInit {
    doctorsWithChecked: DoctorWithChecked[] = [];
    examinationList?: Examination[];
    fileBaseUrl = '';
    fileCover: File | null = null;
    @ViewChild('fileCoverInput') fileCoverInput!: ElementRef;
    formsReady = false;
    deleteDoctorSpecialityUrl = '/admin/delete-doctor-speciality';
    deleteExaminationByIdUrl = '/admin/delete-examination-by-id';
    getAvailableDoctorsUrl = '/admin/getAvailableDoctors';
    getSpecialityByRouterlinkUrl = '/regenero/get-speciality-by-routerLink';
    getSpecialityTypesUrl = '/admin/get-speciality-types';

    htmlForm?: FormGroup;
    maxFileSize = 5000000;
    originalRouterLink = '';
    regeneroBaseUrl = '';

    slugManuallyEdited = false;
    smartyForm?: FormGroup;
    speciality: Speciality = {};
    specialityForm?: FormGroup;
    specialityId = 0;
    specialityInformation?: SpecialityInformation = {
        name: ''
    };
    specialityTypeList: SpecialityType[] = [];
    specialityTypeName = '';
    updateCoverFile = false;
    updateSpecialityUrl = '/admin/update-speciality';
    uploadFileToAnExistingFolderUrl = '/admin/updatefile';
    specialityUrlPrefix = '/consultation';
    private _metaService = inject(MetaService);
    private _route = inject(ActivatedRoute);

    get isFormValid(): boolean {
        return !!this.specialityForm?.valid && !!this.htmlForm?.valid;
    }
    private _dialogService = inject(DialogService);

    constructor(
        public _router: Router,
        private _apiRequestService: ApiRequestService,
        @Inject('REGENERO_BASE_URL') private _regeneroBaseUrl: string,
        private _toastMessageService: ToastmessageService,
        private _fb: FormBuilder,
        private _translateService: TranslationService,
        @Inject('FILE_MANAGER_URL') private _fileManagerUrl: string,
        @Inject('PUBLIC_UI_URL') private _publicUiUrl: string
    ) {
        this.regeneroBaseUrl = _regeneroBaseUrl;
        this.fileBaseUrl = _fileManagerUrl;
        this.createSpecialityForm();
        this.createHtmlForm();
        this.getSpecialityByRouterLink();
        this.createSmartyForm(this.specialityInformation!);
    }

    ngOnInit(): void {
        if (!sessionStorage.getItem('token')) {
            this._router.navigateByUrl('/');
        }
    }

    createSpecialityForm(): void {
        this.specialityForm = this._fb.group({
            name: ['', [Validators.required]],
            jobTitle: ['', [Validators.required]],
            routerLink: ['', [Validators.required]]
        });
    }

    createSmartyForm(specialityInformation: SpecialityInformation): void {
        this.smartyForm = this._fb.group({
            specialityInformationName: [specialityInformation.name, [Validators.required]]
        });
    }

    collectSmartyData(): void {
        this.specialityInformation = {
            name: this.smartyForm?.controls['specialityInformationName'].value as string | undefined,
            smartyText: this.htmlForm?.controls['html'].value as string | undefined
        };
    }

    getSpecialityTypes(): void {
        this._apiRequestService
            .sendRequest<{}, GetSpecialityTypesResponse>(this.regeneroBaseUrl, {}, true, this.getSpecialityTypesUrl)
            .subscribe((response) => {
                this.specialityTypeList = response.specialityTypeList as SpecialityType[];
                this.getDoctorsExcludingSpeciality();
            });
    }

    createHtmlForm(): void {
        this.htmlForm = this._fb.group({
            html: ['', [Validators.required]]
        });
    }

    collectSpecialityData(): void {
        const specialityType: SpecialityType = this.specialityTypeList.filter(
            (specialityType) => specialityType.name === this.specialityTypeName
        )[0];

        const specialityInformation: SpecialityInformation = this.speciality.specialityInformation!;
        const name = (this.specialityForm?.controls['name'].value as string) ?? '';
        let slug = ((this.specialityForm?.controls['routerLink'].value as string) ?? '').trim();
        if (!slug) {
            slug = generateOneSegmentSlug(name);
        }
        this.speciality = {
            id: this.speciality.id,
            name: this.specialityForm?.controls['name'].value as string | undefined,
            jobTitle: this.specialityForm?.controls['jobTitle'].value as string | undefined,
            description: this.htmlForm?.controls['html'].value as string | undefined,
            isSpeciality: true,
            routerLink: slug,
            imageUrl: this.speciality.imageUrl,
            specialityInformation: specialityInformation,
            specialityType: specialityType
        };
    }
    getSpecialityByRouterLink(): void {
        const lastSegmentOfUrl = window.location.href.split('/').pop();
        this.originalRouterLink = lastSegmentOfUrl as string;
        const getSpecialityRequest: GetSpecialityRequest = {
            routerLink: this.originalRouterLink
        };

        this._apiRequestService
            .sendRequest<
                GetSpecialityRequest,
                GetSpecialityResponse
            >(this.regeneroBaseUrl, getSpecialityRequest, true, this.getSpecialityByRouterlinkUrl)
            .subscribe((response) => {
                this.speciality = response.speciality!;
                setTimeout(() => {
                    if (this.specialityForm) {
                        this.specialityForm.patchValue(this.speciality);
                    }
                    this.formsReady = true;
                });

                this.specialityTypeName = this.speciality.specialityType?.name as string;
                this.getSpecialityTypes();
            });
    }
    openAddExaminationDialog(): void {
        this._dialogService
            .openDialog(
                AddexaminationDialogComponent,
                null,
                ((addExaminationDialogResult: AddExaminationDialogResult | undefined) => {
                    if (addExaminationDialogResult?.examination) {
                        addExaminationDialogResult.examination!.specialityId = this.speciality.id;
                        this.examinationList = [...(this.examinationList ?? []), addExaminationDialogResult?.examination!];
                    }
                }
                )
            )
    }
    removeExamination(index: number): void {
        this.examinationList?.splice(index, 1);
    }
    getDoctorsExcludingSpeciality(): void {
        const getAvailableDoctorsUrlRequest: GetgetAvailableDoctorsUrlRequest = {
            specialityId: this.speciality.id
        };
        this._apiRequestService
            .sendRequest<
                GetgetAvailableDoctorsUrlRequest,
                GetgetAvailableDoctorsUrlResponse
            >(this.regeneroBaseUrl, getAvailableDoctorsUrlRequest, true, this.getAvailableDoctorsUrl)
            .subscribe((response) => {
                const doctorsExcludingSpecialityList = response.availableDoctorList as AvailableDoctor[];

                const doctorWithCheckedList: DoctorWithChecked[] = [];
                doctorsExcludingSpecialityList.forEach((doctorsExcludingSpeciality) => {
                    const doctorWithChecked: DoctorWithChecked = {
                        doctor: doctorsExcludingSpeciality,
                        checked: false
                    };
                    doctorWithCheckedList.push(doctorWithChecked);
                });
                this.doctorsWithChecked = [...doctorWithCheckedList];
            });
    }
    handleUpdateSpeciality(): void {
        if (this.fileCover?.size! > this.maxFileSize) {
            this._toastMessageService.displayToastMessage(
                TOAST_STATE.error,
                this._translateService.translate('file.maxSize'),
                MAT_ICON.error
            );
            this._dismissError();
            return;
        }
        if (this.fileCover !== null && this.fileCover.size < this.maxFileSize) {
            const folderName = this.speciality.imageUrl!.split('/').pop() as string;
            this.updateSpecialityWithUpload(this.fileCover, folderName);
        } else {
            this.updateSpeciality().subscribe(() => {
                this.updateMetaTags();
            });
        }
    }
    updateSpecialityWithUpload(file: File, folderName: string): void {
        this._apiRequestService
            .updateFile<any, UploadFileResponse>(file, folderName, true, this.fileBaseUrl, this.uploadFileToAnExistingFolderUrl)
            .pipe(
                switchMap((uploadFileResponse) => {
                    this.speciality.imageUrl = uploadFileResponse.downloadUri;
                    return this.updateSpeciality();
                })
            )
            .subscribe(() => {
                this.updateCoverFile = false;
                this.updateMetaTags();
            });
    }
    updateSpeciality(): Observable<HttpResponse<any>> {
        this.collectSpecialityData();

        if (this.speciality.description == '') {
            this._toastMessageService.displayToastMessage(
                TOAST_STATE.error,
                this._translateService.translate('Kérem adjon leírást a szakrendeléshez!'),
                MAT_ICON.error
            );
            this._dismissError();
            return throwError(() => new Error('Validation failed: Description is required.'));
        }
        const updateSpecialityRequest: UpdateSpecialityRequest = {
            speciality: this.speciality,
            doctorRouterLinkList: this.createDoctorRouterLinkList(),
            examinationList: this.examinationList ?? []
        };

        return this._apiRequestService
            .sendRequest<
                UpdateSpecialityRequest,
                HttpResponse<any>
            >(this.regeneroBaseUrl, updateSpecialityRequest, true, this.updateSpecialityUrl, true);
    }

    updateMetaTags(): void {
        const routerLinkControl = this.specialityForm?.controls['routerLink'];
        const currentRouterLink = ((routerLinkControl?.value as string) ?? '').trim();
        const newRouterLink = currentRouterLink !== this.originalRouterLink ? currentRouterLink : null;
        const canSaveMeta = this._metaService.canSaveMeta(true);
        if (canSaveMeta || newRouterLink) {
            this._metaService.saveMetaTag(true, newRouterLink ?? this.originalRouterLink, this.speciality.imageUrl!, this.originalRouterLink);
        }
        if (newRouterLink) {
            this.originalRouterLink = newRouterLink;
            window.location.href = window.location.href.replace(/[^/]+$/, newRouterLink);
        }
    }
    createDoctorRouterLinkList(): string[] {
        const doctorRouterLinkList: string[] = [];
        this.doctorsWithChecked.forEach((doctor) => {
            if (doctor.checked && doctor.doctor?.routerLink) {
                doctorRouterLinkList.push(doctor.doctor?.routerLink);
            }
        });

        return doctorRouterLinkList;
    }
    changeDoctor(doctorWithChecked: DoctorWithChecked): void {
        this.doctorsWithChecked?.forEach((doctor) => {
            if (doctorWithChecked.doctor?.name === doctor.doctor?.name) {
                doctor.checked = !doctor.checked;
            }
        });
    }
    openDoctorSpecialityDialog(doctorSpeciality: DoctorSpeciality): void {
        const dialogConfiguration: DialogConfiguration = {
            messageKey: 'doctorSpeciality.deleteConfirmMessage',
            targetData: doctorSpeciality.doctor?.name ?? ''
        };

        this._dialogService.openDialog(
            DialogComponent,
            dialogConfiguration,
            () => this._deleteDoctorSpeciality(doctorSpeciality)
        );
    }

    private _deleteDoctorSpeciality(doctorSpeciality: DoctorSpeciality): void {
        const deleteDoctorSpecialityRequest: DeleteDoctorSpecialityRequest = {
            doctorSpecialityId: doctorSpeciality.id
        };

        this._apiRequestService
            .sendRequest<
                DeleteDoctorSpecialityRequest,
                HttpResponse<any>
            >(this.regeneroBaseUrl, deleteDoctorSpecialityRequest, true, this.deleteDoctorSpecialityUrl, true)
            .subscribe(() => {
                this.speciality.doctorSpecialityList = this.speciality.doctorSpecialityList?.filter(
                    (existingDoctorSpeciality) => existingDoctorSpeciality.id !== doctorSpeciality.id
                );
                this._restoreDoctorToAvailable(doctorSpeciality.doctor);
            });
    }

    private _restoreDoctorToAvailable(doctorToRestore?: AvailableDoctor): void {
        if (!doctorToRestore?.routerLink) return;
        const exists = this.doctorsWithChecked.some(
            (item) => item.doctor?.routerLink === doctorToRestore?.routerLink
        );
        if (!exists) {
            this.doctorsWithChecked = [
                ...this.doctorsWithChecked,
                {
                    doctor: doctorToRestore,
                }
            ];
        }
    }

    removeExaminationFromDb(examinationId: number): void {
        const examinationName = this.examinationList?.find((examination) => examination.id === examinationId)?.name ?? '';
        const dialogConfiguration: DialogConfiguration = {
            messageKey: 'examination.deleteConfirmMessage',
            targetData: examinationName
        };

        this._dialogService.openDialog(DialogComponent, dialogConfiguration, () => this._deleteExaminationFromDb(examinationId));
    }

    private _deleteExaminationFromDb(examinationId: number): void {
        const deleteExaminationByIdRequest: DeleteExaminationByIdRequest = {
            examinationId: examinationId
        };

        this._apiRequestService
            .sendRequest<
                DeleteExaminationByIdRequest,
                HttpResponse<any>
            >(this.regeneroBaseUrl, deleteExaminationByIdRequest, true, this.deleteExaminationByIdUrl, true)
            .subscribe(() => {
                this.speciality.examinationList = this.speciality.examinationList?.filter(
                    (existingExamination) => existingExamination.id !== examinationId
                );
            });
    }
    clearSelectedCoverFile(): void {
        this.fileCoverInput.nativeElement.value = null;
        this.updateCoverFile = false;
    }
    onChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.fileCover = target.files![0];
        if (this.fileCover.name.split('.').pop() === 'jpg' || 'png' || 'JPEG' || 'jpeg') {
            this.updateCoverFile = true;
        } else {
            this.updateCoverFile = false;
            this._toastMessageService.displayToastMessage(
                TOAST_STATE.warning,
                'Kérem ".jpg" vagy ".png"+ kiterjesztésű fájlt töltsön fel!',
                MAT_ICON.warning
            );
            this._dismissError();
        }
    }
    onNameBlur(): void {
        const slugControl = this.specialityForm?.controls['routerLink'];
        if (!slugControl || this.slugManuallyEdited) return; // don't overwrite manual entry
        const name = this.specialityForm?.controls['name'].value ?? '';
        if (name) {
            slugControl.setValue(generateOneSegmentSlug(name));
        }
    }
    onSlugInput(): void {
        const slugControl = this.specialityForm?.controls['routerLink'];
        if (!slugControl) return;
        const value = (slugControl.value as string) ?? '';
        if (value.trim() === '') {
            this.slugManuallyEdited = false;
            const name = this.specialityForm?.controls['author'].value ?? '';
            if (name) {
                slugControl.setValue(generateOneSegmentSlug(name));
            }
        } else {
            this.slugManuallyEdited = true;
        }
    }
    shouldShowError(controlName: string): boolean {
        const control = this.specialityForm?.controls[controlName];
        return !!control?.invalid && (!!control?.dirty || !!control?.touched);
    }
    private _dismissError(): void {
        setTimeout(() => {
            this._toastMessageService.dismissToast();
        }, 3000);
    }
}
